import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";

const STORAGE_KEY = "liq_affiliate_ref";
const PARTNER_CACHE = "liq_affiliate_partner";
const DISMISS_KEY = "liq_affiliate_banner_dismissed";
const ATTACHED_KEY = "liq_affiliate_attached";

/**
 * Reads ?ref=CODE from the URL on any public marketing page and, if valid,
 * (a) stores the code in localStorage so signup can attach the referral,
 * (b) fires a fire-and-forget click track,
 * (c) renders a slim discount banner above the fold.
 *
 * Safe to render on every marketing route — no-ops when there's no ref.
 */
export default function AffiliateBanner() {
  const [partner, setPartner] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") return;

    const url = new URL(window.location.href);
    const refFromUrl = (url.searchParams.get("ref") || "").trim();

    // Restore previously stored ref if the URL has none (they navigated deeper).
    const storedRef = localStorage.getItem(STORAGE_KEY);
    const code = refFromUrl || storedRef;
    if (!code) return;

    setDismissed(localStorage.getItem(DISMISS_KEY) === code);

    // If we already validated this exact code in this browser, hydrate from cache
    // to avoid a network flash. Still validate in the background for freshness.
    const cachedRaw = localStorage.getItem(PARTNER_CACHE);
    if (cachedRaw) {
      try {
        const cached = JSON.parse(cachedRaw);
        if (cached && cached.code === code) setPartner(cached);
      } catch {}
    }

    (async () => {
      try {
        const { data } = await http.get(`/affiliate/validate/${encodeURIComponent(code)}`);
        localStorage.setItem(STORAGE_KEY, data.code);
        localStorage.setItem(PARTNER_CACHE, JSON.stringify(data));
        setPartner(data);
        // Only fire the click when we arrived via URL (not localStorage restore).
        if (refFromUrl) {
          http
            .post("/affiliate/click", {
              code: data.code,
              path: url.pathname,
              referrer: document.referrer || "",
            })
            .catch(() => {});
        }
        // Auto-attach to a signed-in existing customer so their upcoming
        // checkout picks up the partner discount. Idempotent + skips admin.
        // We only attempt this once per (user, code) pair via ATTACHED_KEY so
        // repeat visits don't spam the endpoint.
        if (user && user.id) {
          const attachedKey = `${ATTACHED_KEY}:${user.id}:${data.code}`;
          if (!localStorage.getItem(attachedKey)) {
            try {
              const r = await http.post("/affiliate/attach", { code: data.code });
              localStorage.setItem(attachedKey, "1");
              if (r?.data?.attached) {
                toast.success(
                  `${data.name} partner code applied — you'll see ${data.discount_pct}% off at checkout.`,
                  { duration: 6000 }
                );
              }
            } catch {
              // Silent — banner still shows, just no auto-attach.
            }
          }
        }
      } catch {
        // Bad or disabled code — scrub any stale stored value.
        if (refFromUrl) {
          localStorage.removeItem(STORAGE_KEY);
          localStorage.removeItem(PARTNER_CACHE);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!partner || dismissed || !partner.discount_pct) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, partner.code);
    setDismissed(true);
  };

  return (
    <div
      className="w-full bg-ink-900 text-white"
      data-testid="affiliate-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-4">
        <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-white/10 shrink-0">
          <Sparkles className="w-4 h-4 text-brand-300" />
        </div>
        <div className="flex-1 text-sm leading-snug">
          <span className="font-semibold text-brand-300">
            {Math.round(Number(partner.discount_pct) || 0)}% off
          </span>{" "}
          courtesy of{" "}
          <span className="font-semibold text-white">{partner.name}</span>
          <span className="text-white/60 hidden sm:inline">
            {" "}
            · discount is applied automatically at checkout after you sign up.
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1.5 rounded-md hover:bg-white/10 text-white/60 hover:text-white transition-colors shrink-0"
          aria-label="Dismiss partner banner"
          data-testid="affiliate-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
